import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  AlertTriangle, CheckCircle2, XCircle, Users, HardHat, Mail, 
  RefreshCw, Play, Pause, RotateCcw, Search, Loader2,
  Shield, Database, AlertCircle, ChevronRight, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const AuthMigration = () => {
  const { api } = useSuperAdmin();
  const { isDark = true } = useOutletContext() || {};
  
  // State
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [operatorsWithoutEmail, setOperatorsWithoutEmail] = useState([]);
  const [operatorsLoading, setOperatorsLoading] = useState(false);
  const [emailConflicts, setEmailConflicts] = useState([]);
  const [unifiedUsers, setUnifiedUsers] = useState([]);
  const [unifiedUsersLoading, setUnifiedUsersLoading] = useState(false);
  
  // Dialogs
  const [runMigrationDialog, setRunMigrationDialog] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [emailEditDialog, setEmailEditDialog] = useState(false);
  const [editingOperator, setEditingOperator] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  
  // Email conflict resolution state
  const [conflictDetails, setConflictDetails] = useState(null);
  const [conflictDetailsLoading, setConflictDetailsLoading] = useState(false);
  const [conflictResolutionDialog, setConflictResolutionDialog] = useState(false);
  const [selectedConflictEmail, setSelectedConflictEmail] = useState(null);
  const [changeEmailDialog, setChangeEmailDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [newAccountEmail, setNewAccountEmail] = useState('');
  const [savingAccountEmail, setSavingAccountEmail] = useState(false);
  
  // Fetch status
  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/api/super-admin/auth-migration/status');
      setStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch migration status:', err);
      toast.error('Failed to fetch migration status');
    } finally {
      setLoading(false);
    }
  }, [api]);
  
  // Fetch analysis
  const fetchAnalysis = async () => {
    setAnalyzing(true);
    try {
      const res = await api.get('/api/super-admin/auth-migration/analyze');
      setAnalysis(res.data);
      toast.success('Analysis complete');
    } catch (err) {
      console.error('Failed to analyze:', err);
      toast.error('Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };
  
  // Fetch logs
  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await api.get('/api/super-admin/auth-migration/logs?limit=50');
      setLogs(res.data.logs);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLogsLoading(false);
    }
  };
  
  // Fetch operators without email
  const fetchOperatorsWithoutEmail = async () => {
    setOperatorsLoading(true);
    try {
      const res = await api.get('/api/super-admin/auth-migration/operators-without-email?mobile_only=true&limit=100');
      setOperatorsWithoutEmail(res.data.operators);
    } catch (err) {
      console.error('Failed to fetch operators:', err);
    } finally {
      setOperatorsLoading(false);
    }
  };
  
  // Fetch email conflicts
  const fetchEmailConflicts = async () => {
    try {
      const res = await api.get('/api/super-admin/auth-migration/email-conflicts');
      setEmailConflicts(res.data.conflicts);
    } catch (err) {
      console.error('Failed to fetch conflicts:', err);
    }
  };
  
  // Fetch unified users
  const fetchUnifiedUsers = async () => {
    setUnifiedUsersLoading(true);
    try {
      const res = await api.get('/api/super-admin/auth-migration/unified-users?limit=50');
      setUnifiedUsers(res.data.users);
    } catch (err) {
      console.error('Failed to fetch unified users:', err);
    } finally {
      setUnifiedUsersLoading(false);
    }
  };
  
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);
  
  // Run migration
  const runMigration = async () => {
    setMigrating(true);
    try {
      const res = await api.post('/api/super-admin/auth-migration/run', {
        dry_run: dryRun
      });
      toast.success(dryRun ? 'Dry run completed' : 'Migration completed');
      setRunMigrationDialog(false);
      fetchStatus();
      fetchLogs();
      if (!dryRun) {
        fetchUnifiedUsers();
      }
    } catch (err) {
      console.error('Migration failed:', err);
      toast.error(err.response?.data?.detail || 'Migration failed');
    } finally {
      setMigrating(false);
    }
  };
  
  // Rollback
  const rollback = async () => {
    try {
      await api.post('/api/super-admin/auth-migration/rollback');
      toast.success('Rollback successful');
      fetchStatus();
    } catch (err) {
      console.error('Rollback failed:', err);
      toast.error('Rollback failed');
    }
  };
  
  // Update feature flags
  const updateFeatureFlags = async (newAuthEnabled, legacyAuthEnabled) => {
    try {
      await api.put('/api/super-admin/auth-migration/feature-flags', {
        new_auth_enabled: newAuthEnabled,
        legacy_auth_enabled: legacyAuthEnabled
      });
      toast.success('Feature flags updated');
      fetchStatus();
    } catch (err) {
      console.error('Failed to update flags:', err);
      toast.error(err.response?.data?.detail || 'Failed to update flags');
    }
  };
  
  // Save operator email
  const saveOperatorEmail = async () => {
    if (!editingOperator || !newEmail) return;
    
    setSavingEmail(true);
    try {
      await api.put(`/api/super-admin/auth-migration/operators/${editingOperator.id}/email`, {
        operator_id: editingOperator.id,
        email: newEmail
      });
      toast.success('Email updated');
      setEmailEditDialog(false);
      setEditingOperator(null);
      setNewEmail('');
      fetchOperatorsWithoutEmail();
      fetchAnalysis();
    } catch (err) {
      console.error('Failed to update email:', err);
      toast.error(err.response?.data?.detail || 'Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  };
  
  // Fetch conflict details
  const fetchConflictDetails = async (email) => {
    setConflictDetailsLoading(true);
    try {
      const res = await api.get(`/api/super-admin/auth-migration/email-conflicts/${encodeURIComponent(email)}`);
      setConflictDetails(res.data);
      setSelectedConflictEmail(email);
      setConflictResolutionDialog(true);
    } catch (err) {
      console.error('Failed to fetch conflict details:', err);
      toast.error('Failed to fetch conflict details');
    } finally {
      setConflictDetailsLoading(false);
    }
  };
  
  // Change account email (resolve conflict)
  const changeAccountEmail = async () => {
    if (!editingAccount || !newAccountEmail) return;
    
    setSavingAccountEmail(true);
    try {
      await api.post('/api/super-admin/auth-migration/email-conflicts/change-email', {
        account_type: editingAccount.account_type,
        account_id: editingAccount.id,
        old_email: editingAccount.email,
        new_email: newAccountEmail
      });
      toast.success('Email changed successfully');
      setChangeEmailDialog(false);
      setEditingAccount(null);
      setNewAccountEmail('');
      // Refresh conflict details
      if (selectedConflictEmail) {
        fetchConflictDetails(selectedConflictEmail);
      }
      fetchAnalysis();
    } catch (err) {
      console.error('Failed to change email:', err);
      toast.error(err.response?.data?.detail || 'Failed to change email');
    } finally {
      setSavingAccountEmail(false);
    }
  };
  
  // Deactivate account (resolve conflict)
  const deactivateAccount = async (account) => {
    if (!confirm(`Are you sure you want to deactivate this ${account.account_type}? This action can be reversed.`)) {
      return;
    }
    
    try {
      await api.post('/api/super-admin/auth-migration/email-conflicts/deactivate', {
        account_type: account.account_type,
        account_id: account.id
      });
      toast.success('Account deactivated');
      // Refresh conflict details
      if (selectedConflictEmail) {
        fetchConflictDetails(selectedConflictEmail);
      }
      fetchAnalysis();
    } catch (err) {
      console.error('Failed to deactivate account:', err);
      toast.error(err.response?.data?.detail || 'Failed to deactivate account');
    }
  };
  
  // Remove email from account (resolve conflict without replacement email)
  const removeEmail = async (account, currentEmail) => {
    const accountName = account.name || account.full_name || account.username;
    const warningMsg = account.account_type === 'operator' 
      ? `Remove email from ${accountName}? This will disable mobile app access for this operator.`
      : `Remove email from ${accountName}? This user may not be able to log in without an email.`;
    
    if (!confirm(warningMsg)) {
      return;
    }
    
    try {
      const response = await api.post('/api/super-admin/auth-migration/email-conflicts/remove-email', {
        account_type: account.account_type,
        account_id: account.id,
        current_email: currentEmail
      });
      toast.success('Email removed successfully');
      if (response.data.note) {
        toast.info(response.data.note);
      }
      // Refresh conflict details
      if (selectedConflictEmail) {
        fetchConflictDetails(selectedConflictEmail);
      }
      fetchAnalysis();
    } catch (err) {
      console.error('Failed to remove email:', err);
      toast.error(err.response?.data?.detail || 'Failed to remove email');
    }
  };
  
  // Disable mobile access for operator without email
  const disableMobileAccess = async (operator) => {
    if (!confirm(`Disable mobile access for ${operator.name}? They won't be able to use the mobile app until an email is added.`)) {
      return;
    }
    
    try {
      const response = await api.post('/api/super-admin/auth-migration/operators/disable-mobile', {
        operator_id: operator.id
      });
      toast.success(response.data.message || 'Mobile access disabled');
      // Refresh the operators list
      fetchOperatorsWithoutEmail();
      fetchAnalysis();
    } catch (err) {
      console.error('Failed to disable mobile access:', err);
      toast.error(err.response?.data?.detail || 'Failed to disable mobile access');
    }
  };
  
  const getStatusBadge = (migrationStatus) => {
    const colors = {
      not_started: 'bg-slate-500',
      in_progress: 'bg-blue-500',
      completed: 'bg-green-500',
      completed_with_errors: 'bg-orange-500',
      rolled_back: 'bg-red-500'
    };
    return (
      <Badge className={`${colors[migrationStatus] || 'bg-slate-500'} text-white`}>
        {migrationStatus?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN'}
      </Badge>
    );
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Auth Migration Dashboard
          </h1>
          <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>
            Migrate to unified email-based authentication
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchStatus}
            className={isDark ? 'border-slate-600' : ''}
            data-testid="refresh-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="status-cards-grid">
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : ''} data-testid="status-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                <Database className="h-5 w-5 text-[#ffd108]" />
              </div>
              <div>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Status</p>
                <span data-testid="migration-status-badge">{getStatusBadge(status?.status)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : ''} data-testid="tenant-users-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                <Users className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Tenant Users</p>
                <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="tenant-users-count">
                  {status?.live_counts?.tenant_users || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : ''} data-testid="operators-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                <HardHat className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Operators</p>
                <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="operators-count">
                  {status?.live_counts?.operators || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : ''} data-testid="unified-users-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                <Shield className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Unified Users</p>
                <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="unified-users-count">
                  {status?.live_counts?.unified_users || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Feature Flags */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : ''} data-testid="feature-flags-card">
        <CardHeader>
          <CardTitle className={isDark ? 'text-white' : ''}>Feature Flags</CardTitle>
          <CardDescription>Control which authentication systems are active</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="flex items-center gap-3" data-testid="new-auth-toggle-container">
              <Switch
                checked={status?.new_auth_enabled || false}
                onCheckedChange={(checked) => updateFeatureFlags(checked, status?.legacy_auth_enabled)}
                data-testid="new-auth-switch"
              />
              <div>
                <p className={`font-medium ${isDark ? 'text-white' : ''}`}>New Auth (Email-based)</p>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  Enable new unified authentication
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3" data-testid="legacy-auth-toggle-container">
              <Switch
                checked={status?.legacy_auth_enabled !== false}
                onCheckedChange={(checked) => updateFeatureFlags(status?.new_auth_enabled, checked)}
                data-testid="legacy-auth-switch"
              />
              <div>
                <p className={`font-medium ${isDark ? 'text-white' : ''}`}>Legacy Auth (Client ID + Username)</p>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  Keep legacy login working
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Tabs */}
      <Tabs defaultValue="analysis" className="space-y-4" data-testid="migration-tabs">
        <TabsList className={isDark ? 'bg-slate-800' : ''}>
          <TabsTrigger value="analysis" data-testid="tab-analysis">Analysis</TabsTrigger>
          <TabsTrigger value="issues" data-testid="tab-issues">Data Issues</TabsTrigger>
          <TabsTrigger value="migration" data-testid="tab-migration">Run Migration</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Migrated Users</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">Logs</TabsTrigger>
        </TabsList>
        
        {/* Analysis Tab */}
        <TabsContent value="analysis">
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : ''}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className={isDark ? 'text-white' : ''}>Migration Readiness</CardTitle>
                  <CardDescription>Analyze data before running migration</CardDescription>
                </div>
                <Button 
                  onClick={fetchAnalysis} 
                  disabled={analyzing}
                  className="bg-[#ffd108] text-black hover:bg-[#e6bc07]"
                  data-testid="analyze-btn"
                >
                  {analyzing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4 mr-2" />
                  )}
                  Analyze
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {analysis ? (
                <div className="space-y-6" data-testid="analysis-results">
                  {/* Ready Badge */}
                  <div className="flex items-center gap-3" data-testid="readiness-status">
                    {analysis.ready_for_migration ? (
                      <>
                        <CheckCircle2 className="h-6 w-6 text-green-500" />
                        <span className={`text-lg font-medium ${isDark ? 'text-white' : ''}`} data-testid="ready-badge">
                          Ready for Migration
                        </span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-6 w-6 text-red-500" />
                        <span className={`text-lg font-medium ${isDark ? 'text-white' : ''}`} data-testid="not-ready-badge">
                          Not Ready - Fix Issues First
                        </span>
                      </>
                    )}
                  </div>
                  
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="analysis-summary-grid">
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`} data-testid="analysis-tenant-users">
                      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Tenant Users</p>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : ''}`}>
                        {analysis.summary?.total_tenant_users || 0}
                      </p>
                    </div>
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`} data-testid="analysis-operators">
                      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Operators</p>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : ''}`}>
                        {analysis.summary?.total_operators || 0}
                      </p>
                    </div>
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`} data-testid="analysis-ops-with-email">
                      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Ops with Email</p>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : ''}`}>
                        {analysis.summary?.operators_with_email || 0}
                      </p>
                    </div>
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`} data-testid="analysis-email-conflicts">
                      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Email Conflicts</p>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : ''}`}>
                        {analysis.summary?.email_conflicts || 0}
                      </p>
                    </div>
                  </div>
                  
                  {/* Blocking Issues */}
                  {analysis.blocking_issues?.length > 0 && (
                    <div className={`p-4 rounded-lg border ${isDark ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'}`} data-testid="blocking-issues-container">
                      <h4 className="font-medium text-red-500 mb-2 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Blocking Issues
                      </h4>
                      <ul className="space-y-2" data-testid="blocking-issues-list">
                        {analysis.blocking_issues.map((issue, idx) => (
                          <li key={idx} className={`text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`} data-testid={`blocking-issue-${idx}`}>
                            • {issue.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Email Conflicts */}
                  {analysis.email_conflicts?.length > 0 && (
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                      <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : ''}`}>
                        Email Conflicts ({analysis.email_conflicts.length})
                      </h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {analysis.email_conflicts.slice(0, 10).map((conflict, idx) => (
                          <div key={idx} className={`text-sm p-2 rounded ${isDark ? 'bg-slate-600/50' : 'bg-white'}`}>
                            <span className="font-mono">{conflict.email}</span>
                            <span className={`ml-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                              ({conflict.account_count} accounts across {conflict.tenant_count} tenants)
                            </span>
                            {conflict.same_tenant_conflict && (
                              <Badge className="ml-2 bg-red-500 text-white text-xs">Same Tenant</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  Click "Analyze" to check migration readiness
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Data Issues Tab */}
        <TabsContent value="issues" data-testid="data-issues-tab-content">
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : ''} data-testid="operators-missing-email-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className={isDark ? 'text-white' : ''}>Operators Missing Email</CardTitle>
                  <CardDescription>Mobile-enabled operators must have email addresses</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  onClick={fetchOperatorsWithoutEmail}
                  disabled={operatorsLoading}
                  className={isDark ? 'border-slate-600' : ''}
                  data-testid="load-operators-btn"
                >
                  {operatorsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Load'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {operatorsWithoutEmail.length > 0 ? (
                <Table data-testid="operators-without-email-table">
                  <TableHeader>
                    <TableRow className={isDark ? 'border-slate-700' : ''}>
                      <TableHead className={isDark ? 'text-slate-300' : ''}>Name</TableHead>
                      <TableHead className={isDark ? 'text-slate-300' : ''}>Tenant</TableHead>
                      <TableHead className={isDark ? 'text-slate-300' : ''}>Code</TableHead>
                      <TableHead className={isDark ? 'text-slate-300' : ''}>Phone</TableHead>
                      <TableHead className={isDark ? 'text-slate-300' : ''}>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-testid="operators-table-body">
                    {operatorsWithoutEmail.map((op) => (
                      <TableRow key={op.id} className={isDark ? 'border-slate-700' : ''} data-testid={`operator-row-${op.id}`}>
                        <TableCell className={isDark ? 'text-white' : ''}>{op.name}</TableCell>
                        <TableCell className={isDark ? 'text-slate-300' : ''}>
                          {op.company_name} ({op.client_id})
                        </TableCell>
                        <TableCell className={`font-mono ${isDark ? 'text-slate-300' : ''}`}>
                          {op.operator_code || '-'}
                        </TableCell>
                        <TableCell className={isDark ? 'text-slate-300' : ''}>
                          {op.phone || '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => {
                                setEditingOperator(op);
                                setNewEmail('');
                                setEmailEditDialog(true);
                              }}
                              data-testid={`add-email-btn-${op.id}`}
                            >
                              <Mail className="h-3 w-3 mr-1" />
                              Add Email
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => disableMobileAccess(op)}
                              className={isDark ? 'border-slate-600' : ''}
                              data-testid={`disable-mobile-btn-${op.id}`}
                            >
                              Disable Mobile
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-testid="no-operators-message">
                  {operatorsLoading ? 'Loading...' : 'Click "Load" to check for operators without email'}
                </p>
              )}
            </CardContent>
          </Card>
          
          {/* Email Conflicts Section */}
          {analysis?.email_conflicts?.length > 0 && (
            <Card className={`mt-6 ${isDark ? 'bg-slate-800/50 border-slate-700' : ''}`} data-testid="email-conflicts-card">
              <CardHeader>
                <CardTitle className={isDark ? 'text-white' : ''} data-testid="email-conflicts-title">
                  <AlertTriangle className="h-5 w-5 inline mr-2 text-orange-500" />
                  Email Conflicts ({analysis.email_conflicts.length})
                </CardTitle>
                <CardDescription>
                  Same email used by multiple accounts - resolve before migration
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3" data-testid="email-conflicts-list">
                  {analysis.email_conflicts.map((conflict, idx) => (
                    <div 
                      key={idx} 
                      className={`p-4 rounded-lg border ${
                        conflict.same_tenant_conflict 
                          ? isDark ? 'border-red-500/50 bg-red-500/10' : 'border-red-300 bg-red-50'
                          : isDark ? 'border-slate-600 bg-slate-700/50' : 'border-gray-200 bg-gray-50'
                      }`}
                      data-testid={`email-conflict-item-${idx}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className={`font-mono ${isDark ? 'text-white' : ''}`} data-testid={`conflict-email-${idx}`}>{conflict.email}</span>
                          <span className={`ml-2 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                            ({conflict.account_count} accounts across {conflict.tenant_count} tenant{conflict.tenant_count > 1 ? 's' : ''})
                          </span>
                          {conflict.same_tenant_conflict && (
                            <Badge className="ml-2 bg-red-500 text-white text-xs" data-testid={`same-tenant-badge-${idx}`}>Same Tenant - Must Resolve</Badge>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => fetchConflictDetails(conflict.email)}
                          disabled={conflictDetailsLoading}
                          className={isDark ? 'border-slate-600' : ''}
                          data-testid={`resolve-conflict-btn-${idx}`}
                        >
                          {conflictDetailsLoading && selectedConflictEmail === conflict.email ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Resolve'
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* Run Migration Tab */}
        <TabsContent value="migration">
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : ''}>
            <CardHeader>
              <CardTitle className={isDark ? 'text-white' : ''}>Execute Migration</CardTitle>
              <CardDescription>Run the migration process</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button
                  onClick={() => {
                    setDryRun(true);
                    setRunMigrationDialog(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={migrating}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Dry Run
                </Button>
                
                <Button
                  onClick={() => {
                    setDryRun(false);
                    setRunMigrationDialog(true);
                  }}
                  className="bg-green-600 hover:bg-green-700"
                  disabled={migrating || !analysis?.ready_for_migration}
                >
                  <Play className="h-4 w-4 mr-2" />
                  Run Migration
                </Button>
                
                <Button
                  onClick={rollback}
                  variant="destructive"
                  disabled={status?.status === 'not_started'}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Rollback
                </Button>
              </div>
              
              {/* Progress */}
              {status?.status === 'in_progress' && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className={isDark ? 'text-slate-400' : ''}>Migration Progress</span>
                    <span className={isDark ? 'text-white' : ''}>
                      {status.migrated_tenant_users + status.migrated_operators} / {status.total_tenant_users + status.total_operators}
                    </span>
                  </div>
                  <Progress 
                    value={((status.migrated_tenant_users + status.migrated_operators) / 
                           (status.total_tenant_users + status.total_operators || 1)) * 100} 
                  />
                </div>
              )}
              
              {/* Warnings */}
              {!analysis?.ready_for_migration && analysis && (
                <div className={`p-4 rounded-lg ${isDark ? 'bg-orange-500/10 border border-orange-500/30' : 'bg-orange-50 border border-orange-200'}`}>
                  <p className="text-orange-500 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Migration blocked until all issues are resolved. Run "Analyze" to see details.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Migrated Users Tab */}
        <TabsContent value="users">
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : ''}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className={isDark ? 'text-white' : ''}>Unified Users</CardTitle>
                  <CardDescription>Users migrated to the new authentication system</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  onClick={fetchUnifiedUsers}
                  disabled={unifiedUsersLoading}
                  className={isDark ? 'border-slate-600' : ''}
                >
                  {unifiedUsersLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Load'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {unifiedUsers.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className={isDark ? 'border-slate-700' : ''}>
                      <TableHead className={isDark ? 'text-slate-300' : ''}>Email</TableHead>
                      <TableHead className={isDark ? 'text-slate-300' : ''}>Name</TableHead>
                      <TableHead className={isDark ? 'text-slate-300' : ''}>Tenants</TableHead>
                      <TableHead className={isDark ? 'text-slate-300' : ''}>Source</TableHead>
                      <TableHead className={isDark ? 'text-slate-300' : ''}>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {unifiedUsers.map((user) => (
                      <TableRow key={user.id} className={isDark ? 'border-slate-700' : ''}>
                        <TableCell className={`font-mono ${isDark ? 'text-white' : ''}`}>
                          {user.email}
                        </TableCell>
                        <TableCell className={isDark ? 'text-slate-300' : ''}>
                          {user.full_name}
                        </TableCell>
                        <TableCell className={isDark ? 'text-slate-300' : ''}>
                          {user.tenant_access?.length || 0} tenant(s)
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={isDark ? 'border-slate-600' : ''}>
                            {user.migrated_from || 'manual'}
                          </Badge>
                        </TableCell>
                        <TableCell className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                          {user.created_at && formatDistanceToNow(new Date(user.created_at), { addSuffix: true })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  {unifiedUsersLoading ? 'Loading...' : 'No unified users yet. Run migration to create them.'}
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Logs Tab */}
        <TabsContent value="logs">
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : ''}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className={isDark ? 'text-white' : ''}>Migration Logs</CardTitle>
                  <CardDescription>Audit trail of migration actions</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  onClick={fetchLogs}
                  disabled={logsLoading}
                  className={isDark ? 'border-slate-600' : ''}
                >
                  {logsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {logs.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {logs.map((log) => (
                    <div 
                      key={log.id} 
                      className={`p-3 rounded-lg text-sm ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}
                    >
                      <div className="flex items-center gap-2">
                        {log.status === 'success' && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                        {log.status === 'failed' && <XCircle className="h-4 w-4 text-red-500" />}
                        {log.status === 'skipped' && <AlertCircle className="h-4 w-4 text-orange-500" />}
                        <span className={`font-medium ${isDark ? 'text-white' : ''}`}>
                          {log.action?.replace(/_/g, ' ')}
                        </span>
                        <Badge variant="outline" className={`text-xs ${isDark ? 'border-slate-600' : ''}`}>
                          {log.source_type}
                        </Badge>
                        <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                          {log.source_client_id}
                        </span>
                      </div>
                      {log.message && (
                        <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                          {log.message}
                        </p>
                      )}
                      <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                        {log.created_at && formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                        {log.dry_run && <span className="ml-2 text-blue-400">(dry run)</span>}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  {logsLoading ? 'Loading...' : 'No logs yet'}
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Run Migration Dialog */}
      <Dialog open={runMigrationDialog} onOpenChange={setRunMigrationDialog}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>
              {dryRun ? 'Dry Run Migration' : 'Run Migration'}
            </DialogTitle>
            <DialogDescription>
              {dryRun 
                ? 'This will simulate the migration without making any changes.'
                : 'This will migrate all users and operators to the new unified system.'}
            </DialogDescription>
          </DialogHeader>
          
          {!dryRun && (
            <div className={`p-4 rounded-lg ${isDark ? 'bg-orange-500/10 border border-orange-500/30' : 'bg-orange-50 border border-orange-200'}`}>
              <p className="text-orange-500 flex items-center gap-2 font-medium">
                <AlertTriangle className="h-4 w-4" />
                Warning
              </p>
              <p className={`text-sm mt-1 ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                This action will create unified user records. Make sure you have run a dry run first.
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRunMigrationDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={runMigration}
              disabled={migrating}
              className={dryRun ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}
            >
              {migrating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : dryRun ? (
                <Eye className="h-4 w-4 mr-2" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              {dryRun ? 'Start Dry Run' : 'Start Migration'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Email Dialog */}
      <Dialog open={emailEditDialog} onOpenChange={setEmailEditDialog}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : ''} data-testid="add-email-dialog">
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''} data-testid="add-email-dialog-title">
              Add Email for {editingOperator?.name}
            </DialogTitle>
            <DialogDescription>
              Enter an email address for this operator
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className={isDark ? 'text-white' : ''}>Email Address</Label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="operator@company.com"
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
                data-testid="add-email-input"
              />
            </div>
            
            {editingOperator?.phone && (
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Phone: {editingOperator.phone}
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailEditDialog(false)} data-testid="add-email-cancel-btn">
              Cancel
            </Button>
            <Button 
              onClick={saveOperatorEmail}
              disabled={savingEmail || !newEmail}
              className="bg-[#ffd108] text-black hover:bg-[#e6bc07]"
              data-testid="add-email-save-btn"
            >
              {savingEmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Email Conflict Resolution Dialog */}
      <Dialog open={conflictResolutionDialog} onOpenChange={setConflictResolutionDialog}>
        <DialogContent className={`max-w-2xl ${isDark ? 'bg-slate-800 border-slate-700' : ''}`} data-testid="conflict-resolution-dialog">
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''} data-testid="conflict-resolution-dialog-title">
              Email Details: {selectedConflictEmail}
            </DialogTitle>
            <DialogDescription>
              {conflictDetails?.has_real_conflict 
                ? "This email has duplicate accounts of the same type that must be resolved."
                : "This email is used by multiple accounts but can be safely merged during migration."}
            </DialogDescription>
          </DialogHeader>
          
          {conflictDetails && (
            <div className="space-y-4 max-h-96 overflow-y-auto" data-testid="conflict-details-content">
              {/* Real Conflict Warning */}
              {conflictDetails.has_real_conflict && (
                <div className={`p-3 rounded-lg ${isDark ? 'bg-red-500/10 border border-red-500/30' : 'bg-red-50 border border-red-200'}`} data-testid="real-conflict-warning">
                  <p className="text-red-500 text-sm font-medium">
                    <AlertTriangle className="h-4 w-4 inline mr-1" />
                    Duplicate accounts found - must resolve before migration
                  </p>
                  <ul className="mt-2 text-sm text-red-400">
                    {conflictDetails.real_conflicts?.map((c, idx) => (
                      <li key={idx}>
                        {c.has_user_conflict && `${c.users.length} duplicate users in ${c.company_name}`}
                        {c.has_operator_conflict && `${c.operators.length} duplicate operators in ${c.company_name}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Merge Info (no conflict) */}
              {!conflictDetails.has_real_conflict && conflictDetails.merge_candidates?.length > 0 && (
                <div className={`p-3 rounded-lg ${isDark ? 'bg-green-500/10 border border-green-500/30' : 'bg-green-50 border border-green-200'}`} data-testid="merge-candidate-info">
                  <p className="text-green-500 text-sm font-medium">
                    <CheckCircle2 className="h-4 w-4 inline mr-1" />
                    No conflict - These accounts will be merged into a single unified user
                  </p>
                  <p className={`mt-1 text-xs ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                    The tenant user and operator(s) with the same email represent the same person with different access levels.
                  </p>
                </div>
              )}
              
              {/* Users with this email */}
              {conflictDetails.users?.length > 0 && (
                <div data-testid="conflict-users-section">
                  <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : ''}`}>
                    <Users className="h-4 w-4 inline mr-1" />
                    Tenant Users ({conflictDetails.users.length})
                    {conflictDetails.users.length > 1 && (
                      <Badge className="ml-2 bg-red-500 text-white text-xs" data-testid="users-duplicate-badge">Duplicate!</Badge>
                    )}
                  </h4>
                  <div className="space-y-2">
                    {conflictDetails.users.map((user) => (
                      <div 
                        key={user.id} 
                        className={`p-3 rounded-lg border ${isDark ? 'border-slate-600 bg-slate-700/50' : 'border-gray-200 bg-gray-50'}`}
                        data-testid={`conflict-user-${user.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={isDark ? 'text-white' : ''}>
                              {user.full_name || user.username}
                              {!user.is_active && <Badge className="ml-2 bg-gray-500 text-xs">Inactive</Badge>}
                            </p>
                            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                              {user.company_name} ({user.client_id}) • {user.role}
                            </p>
                          </div>
                          {conflictDetails.users.length > 1 && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingAccount({ ...user, account_type: 'user' });
                                  setNewAccountEmail('');
                                  setChangeEmailDialog(true);
                                }}
                                className={isDark ? 'border-slate-600' : ''}
                                data-testid={`change-email-user-${user.id}`}
                              >
                                Change Email
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => removeEmail({ ...user, account_type: 'user' }, selectedConflictEmail)}
                                className={`${isDark ? 'border-orange-600 text-orange-400 hover:bg-orange-500/20' : 'border-orange-400 text-orange-600 hover:bg-orange-50'}`}
                                data-testid={`remove-email-user-${user.id}`}
                              >
                                Remove Email
                              </Button>
                              {user.is_active && (
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => deactivateAccount({ ...user, account_type: 'user' })}
                                  data-testid={`deactivate-user-${user.id}`}
                                >
                                  Deactivate
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Operators with this email */}
              {conflictDetails.operators?.length > 0 && (
                <div data-testid="conflict-operators-section">
                  <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : ''}`}>
                    <HardHat className="h-4 w-4 inline mr-1" />
                    Operators ({conflictDetails.operators.length})
                    {conflictDetails.operators.length > 1 && (
                      <Badge className="ml-2 bg-red-500 text-white text-xs" data-testid="operators-duplicate-badge">Duplicate!</Badge>
                    )}
                  </h4>
                  <div className="space-y-2">
                    {conflictDetails.operators.map((op) => (
                      <div 
                        key={op.id} 
                        className={`p-3 rounded-lg border ${isDark ? 'border-slate-600 bg-slate-700/50' : 'border-gray-200 bg-gray-50'}`}
                        data-testid={`conflict-operator-${op.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={isDark ? 'text-white' : ''}>
                              {op.name}
                              {op.mobile_enabled && <Badge className="ml-2 bg-blue-500 text-xs">Mobile</Badge>}
                            </p>
                            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                              {op.company_name} ({op.client_id}) • Code: {op.operator_code || 'N/A'}
                            </p>
                          </div>
                          {conflictDetails.operators.length > 1 && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingAccount({ ...op, account_type: 'operator' });
                                  setNewAccountEmail('');
                                  setChangeEmailDialog(true);
                                }}
                                className={isDark ? 'border-slate-600' : ''}
                                data-testid={`change-email-operator-${op.id}`}
                              >
                                Change Email
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => removeEmail({ ...op, account_type: 'operator' }, selectedConflictEmail)}
                                className={`${isDark ? 'border-orange-600 text-orange-400 hover:bg-orange-500/20' : 'border-orange-400 text-orange-600 hover:bg-orange-50'}`}
                                data-testid={`remove-email-operator-${op.id}`}
                              >
                                Remove Email
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => deactivateAccount({ ...op, account_type: 'operator' })}
                                data-testid={`deactivate-operator-${op.id}`}
                              >
                                Deactivate
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setConflictResolutionDialog(false)} data-testid="close-conflict-dialog-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Change Account Email Dialog */}
      <Dialog open={changeEmailDialog} onOpenChange={setChangeEmailDialog}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : ''} data-testid="change-email-dialog">
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''} data-testid="change-email-dialog-title">
              Change Email for {editingAccount?.name || editingAccount?.full_name || editingAccount?.username}
            </DialogTitle>
            <DialogDescription>
              Enter a new email address to resolve the conflict
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`} data-testid="current-email-info">
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Current email: <span className="font-mono">{editingAccount?.email}</span>
              </p>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Account type: {editingAccount?.account_type}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className={isDark ? 'text-white' : ''}>New Email Address</Label>
              <Input
                type="email"
                value={newAccountEmail}
                onChange={(e) => setNewAccountEmail(e.target.value)}
                placeholder="new.email@company.com"
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
                data-testid="new-email-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeEmailDialog(false)} data-testid="change-email-cancel-btn">
              Cancel
            </Button>
            <Button 
              onClick={changeAccountEmail}
              disabled={savingAccountEmail || !newAccountEmail}
              className="bg-[#ffd108] text-black hover:bg-[#e6bc07]"
              data-testid="change-email-save-btn"
            >
              {savingAccountEmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Change Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuthMigration;
